import { CheckCircle2, AlertTriangle, User, Hash, Tag, UserPlus, Link, Mail, Phone } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Upload, UnmatchedIdentifierAction, DriverMatchResult } from '@/types'
import { DriverMaster } from '@/types/driverMaster'

interface SkippedStepInfo {
  reason: string
  details: string
}

interface DriverReviewStepProps {
  upload?: Upload
  driverMatches: DriverMatchResult[]
  availableDrivers: DriverMaster[]
  unmatchedActions: Map<number, UnmatchedIdentifierAction>
  perfectMatchOverrides: Map<number, UnmatchedIdentifierAction>
  ignoredRows: Set<number>
  autoAcceptMatches: boolean
  skippedFieldMapping?: SkippedStepInfo // NEW: notification if field mapping was skipped
  onUnmatchedActionChange: (rowIndex: number, action: 'create_new' | 'select_existing', data?: { selectedDriverId?: string; newDriverName?: string }) => void
  onPerfectMatchOverrideChange: (rowIndex: number, action: 'create_new' | 'select_existing', data?: { selectedDriverId?: string; newDriverName?: string }) => void
  onRemovePerfectMatchOverride: (rowIndex: number) => void
  onIgnoreRow: (rowIndex: number) => void
  onUnignoreRow: (rowIndex: number) => void
  onAutoAcceptMatchesChange: (autoAccept: boolean) => void
}

// Helper function to get match type icon and color
// matchedVia indicates which identifier was actually used for matching (uuid, name, email, phone)
const getMatchTypeIcon = (matchedVia?: string) => {
  switch (matchedVia) {
    case 'uuid': return { icon: Hash, color: 'text-blue-600', label: 'UUID' }
    case 'name': return { icon: User, color: 'text-green-600', label: 'Namn' }
    case 'email': return { icon: Mail, color: 'text-purple-600', label: 'E-post' }
    case 'phone': return { icon: Phone, color: 'text-orange-600', label: 'Telefon' }
    default: return { icon: Tag, color: 'text-gray-600', label: 'Okänd' }
  }
}

// Match indicator component
const MatchIndicator = ({ item }: { item: any }) => {
  const matchInfo = getMatchTypeIcon(item.matchedVia)
  const Icon = matchInfo.icon

  // Get match score styling based on score
  const getMatchScoreStyle = (score?: number) => {
    if (!score) return null

    if (score === 100) {
      return { color: 'text-green-700', bg: 'bg-green-100', text: '100%' }
    } else if (score >= 90) {
      return { color: 'text-blue-700', bg: 'bg-blue-100', text: `${score}%` }
    } else if (score >= 80) {
      return { color: 'text-blue-600', bg: 'bg-blue-50', text: `${score}%` }
    } else if (score >= 70) {
      return { color: 'text-yellow-700', bg: 'bg-yellow-100', text: `${score}%` }
    }
    return null
  }

  const matchScoreStyle = getMatchScoreStyle(item.matchScore)

  // Determine the alias status
  const getAliasStatus = () => {
    if (item.aliasExists) {
      return {
        text: 'Alias finns',
        icon: CheckCircle2,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200'
      }
    } else if (item.needsAlias) {
      return {
        text: 'Lägger till alias',
        icon: UserPlus,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      }
    }
    return null
  }

  const aliasStatus = getAliasStatus()

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-xs">
        <div className={`flex items-center gap-1 ${matchInfo.color}`}>
          <Icon className="h-3 w-3" />
          <span className="font-medium">{matchInfo.label}</span>
        </div>
        {matchScoreStyle && (
          <Badge
            variant="outline"
            className={`text-xs px-1.5 py-0.5 font-semibold ${matchScoreStyle.color} ${matchScoreStyle.bg} border-current`}
          >
            {matchScoreStyle.text}
            {item.fuzzyMatch && <span className="ml-0.5 opacity-70">≈</span>}
          </Badge>
        )}
        {item.matchedVia === 'alias' && (
          <div className="flex items-center gap-1 text-blue-600">
            <Link className="h-3 w-3" />
            <span>Alias</span>
          </div>
        )}
        {item.matchedVia === 'primary_name' && (
          <div className="flex items-center gap-1 text-green-600">
            <User className="h-3 w-3" />
            <span>Primärt</span>
          </div>
        )}
      </div>
      {aliasStatus && (
        <Badge
          variant="outline"
          className={`text-xs px-2 py-0.5 ${aliasStatus.color} ${aliasStatus.bgColor} ${aliasStatus.borderColor}`}
        >
          <aliasStatus.icon className="h-3 w-3 mr-1" />
          {aliasStatus.text}
        </Badge>
      )}
    </div>
  )
}

export function DriverReviewStep({
  upload,
  driverMatches,
  availableDrivers,
  unmatchedActions,
  perfectMatchOverrides,
  ignoredRows,
  autoAcceptMatches,
  skippedFieldMapping,
  onUnmatchedActionChange,
  onPerfectMatchOverrideChange,
  onRemovePerfectMatchOverride,
  onIgnoreRow,
  onUnignoreRow,
  onAutoAcceptMatchesChange,
}: DriverReviewStepProps) {
  if (!driverMatches || driverMatches.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Väntar på förar-matchning...</p>
      </div>
    )
  }

  // Separate matches by actual match identifier used (not by UUID presence in data)
  //
  // IMPORTANT: We categorize based on matchedVia (which identifier was used for matching),
  // NOT based on whether identifiers.uuid exists in the uploaded data.
  //
  // Example scenario that would be a bug:
  // - Upload has UUID "abc123" in the data (identifiers.uuid exists)
  // - But UUID "abc123" doesn't match any driver alias
  // - Matching falls back to name matching and succeeds
  // - Result: matchedVia = 'name', NOT 'uuid'
  // - This should appear in "nonUuidMatches" (requires review), not "uuidMatches" (certain)

  const uuidMatches = driverMatches.filter(item =>
    item.hasMatch && item.matchedVia === 'uuid' && !perfectMatchOverrides.has(item.rowIndex)
  )
  const nonUuidMatches = driverMatches.filter(item =>
    item.hasMatch && item.matchedVia !== 'uuid' && !perfectMatchOverrides.has(item.rowIndex)
  )
  const noMatches = driverMatches.filter(item =>
    !item.hasMatch || perfectMatchOverrides.has(item.rowIndex)
  )

  // Check if ALL drivers were matched via UUID (most certain match type)
  // Note: We check matchedVia === 'uuid', not whether UUID exists in the uploaded data
  // A row might have UUID in data but match via name if the UUID doesn't match any driver
  const allMatchedByUuid = driverMatches.length > 0 &&
    driverMatches.every(item => item.hasMatch && item.matchedVia === 'uuid') &&
    perfectMatchOverrides.size === 0

  return (
    <div className="space-y-6">
      {/* Field mapping skipped notification */}
      {skippedFieldMapping && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">{skippedFieldMapping.reason}</AlertTitle>
          <AlertDescription className="text-green-700">
            {skippedFieldMapping.details}
          </AlertDescription>
        </Alert>
      )}

      {/* Success message for all UUID matches */}
      {allMatchedByUuid && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-green-800">
              <CheckCircle2 className="h-6 w-6" />
              <div>
                <p className="font-medium">Alla förare finns redan i systemet och är identifierade</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Aggregated SumUp Data Information */}
      {upload?.aggregatedData && upload?.platform === 'sumup' && (
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-purple-800">
              <Tag className="h-6 w-6" />
              <div>
                <p className="font-medium">SumUp transaktionsdata aggregerad</p>
                <p className="text-sm text-purple-700">
                  {upload?.aggregatedData?.processingStats?.originalRows} transaktioner aggregerade till {upload?.aggregatedData?.processingStats?.finalRecords} förarrecord
                  ({upload?.aggregatedData?.processingStats?.tipRows} dricks, {upload?.aggregatedData?.processingStats?.incomeRows} inkomster)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Non-UUID Matches Section */}
      {nonUuidMatches.length > 0 && (
        <Card className="border-blue-200">
          <CardHeader>
            <CardTitle className="text-blue-800 flex items-center gap-2">
              <User className="h-5 w-5" />
              Matchade förare - Kräver granskning ({nonUuidMatches.length})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Checkbox
                id="auto-accept"
                checked={autoAcceptMatches}
                onCheckedChange={(checked) => onAutoAcceptMatchesChange(!!checked)}
              />
              <Label htmlFor="auto-accept" className="text-sm text-muted-foreground">
                Acceptera alla matchningar automatiskt (skapar alias där det behövs)
              </Label>
            </div>
          </CardHeader>
          <CardContent>
            <div className="max-h-48 overflow-auto border rounded-md">
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Matchad förare</TableHead>
                    <TableHead>Match detaljer</TableHead>
                    <TableHead>Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {nonUuidMatches.map((item) => {
                    const override = perfectMatchOverrides.get(item.rowIndex)
                    const isIgnored = ignoredRows.has(item.rowIndex)
                    return (
                      <TableRow key={item.rowIndex} className={isIgnored ? 'opacity-50' : ''}>
                        <TableCell>
                          <div className="font-medium">
                            {item.extractedName || item.identifiers.name || 'Okänt namn'}
                          </div>
                        </TableCell>
                        <TableCell>
                          {override ? (
                            <Badge variant="outline" className="text-orange-700 bg-orange-50">
                              {override.action === 'create_new' ? 'Skapar ny förare' : 'Omatchad'}
                            </Badge>
                          ) : isIgnored ? (
                            <Badge variant="outline" className="text-gray-500 bg-gray-50">
                              Ignorerad
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-green-700 bg-green-50">
                              {item.matchedDriverName ||
                               (item.matchedDriverId ? availableDrivers.find(d => d.id === item.matchedDriverId)?.primaryName : null) ||
                               'Okänd'}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {!override && !isIgnored && item.hasMatch && (
                            <MatchIndicator item={item} />
                          )}
                        </TableCell>
                        <TableCell>
                          {isIgnored ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onUnignoreRow(item.rowIndex)}
                              className="text-xs px-2 py-1 h-auto text-blue-600 hover:text-blue-700"
                            >
                              Ångra ignorera
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Select
                                value={
                                  override ? override.action :
                                  'accept'
                                }
                                onValueChange={(action) => {
                                  if (action === 'ignore') {
                                    onIgnoreRow(item.rowIndex)
                                  } else if (action === 'create_new') {
                                    const newDriverName = item.extractedName || item.identifiers.name || `Driver ${item.rowIndex + 1}`
                                    onPerfectMatchOverrideChange(item.rowIndex, 'create_new', { newDriverName })
                                  } else if (action === 'accept') {
                                    onRemovePerfectMatchOverride(item.rowIndex)
                                  } else if (action === 'link') {
                                    onPerfectMatchOverrideChange(item.rowIndex, 'select_existing', { selectedDriverId: '' })
                                  }
                                }}
                              >
                                <SelectTrigger className="h-auto py-1 px-2 text-xs w-40">
                                  <SelectValue>
                                    {override ?
                                      (override.action === 'create_new' ? 'Skapa ny förare' :
                                       override.action === 'select_existing' ? 'Länka till förare' : 'Acceptera match') :
                                      'Acceptera match'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="accept">Acceptera match</SelectItem>
                                  <SelectItem value="create_new">Skapa ny förare</SelectItem>
                                  <SelectItem value="link">Länka till förare</SelectItem>
                                  <SelectItem value="ignore">Ignorera förare</SelectItem>
                                </SelectContent>
                              </Select>

                              {override?.action === 'select_existing' && (
                                <Select
                                  value={override.selectedDriverId || ''}
                                  onValueChange={(driverId) => {
                                    onPerfectMatchOverrideChange(item.rowIndex, 'select_existing', { selectedDriverId: driverId })
                                  }}
                                >
                                  <SelectTrigger className="h-auto py-1 px-2 text-xs w-40">
                                    <SelectValue placeholder="Välj förare" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableDrivers.filter(driver => driver.id !== item.matchedDriverId).map(driver => (
                                      <SelectItem key={driver.id} value={driver.id}>
                                        {driver.primaryName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* UUID Matches Section - Collapsible */}
      {uuidMatches.length > 0 && (
        <Accordion type="single" collapsible>
          <AccordionItem value="uuid-matches">
            <Card className="border-green-200">
              <CardHeader className="pb-0">
                <AccordionTrigger className="hover:no-underline">
                  <CardTitle className="text-green-800 flex items-center gap-2">
                    <Hash className="h-5 w-5" />
                    UUID matchningar - Säkra ({uuidMatches.length})
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <div className="text-sm text-green-700 mb-3">
                    Dessa förare matchades säkert med UUID och kräver ingen granskning.
                  </div>
                  <div className="max-h-32 overflow-auto border rounded-md">
                    <div className="overflow-x-auto">
                      <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Namn</TableHead>
                          <TableHead>Matchad förare</TableHead>
                          <TableHead>UUID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {uuidMatches.map((item) => (
                          <TableRow key={item.rowIndex}>
                            <TableCell>
                              <div className="font-medium">
                                {item.extractedName || item.identifiers.name || 'Okänt namn'}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-green-700 bg-green-50">
                                {item.matchedDriverName ||
                                 (item.matchedDriverId ? availableDrivers.find(d => d.id === item.matchedDriverId)?.primaryName : null) ||
                                 'Okänd'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="font-mono text-xs text-blue-600">
                                {item.identifiers.uuid}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      </Table>
                    </div>
                  </div>
                </CardContent>
              </AccordionContent>
            </Card>
          </AccordionItem>
        </Accordion>
      )}

      {/* No Matches Section */}
      {noMatches.length > 0 && (
        <Card className="border-yellow-200">
          <CardHeader>
            <CardTitle className="text-yellow-800 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Ingen matchning ({noMatches.length}) - Åtgärd krävs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-64 overflow-auto border rounded-md">
              <div className="overflow-x-auto">
                <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Namn</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Åtgärd</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {noMatches.map((item) => {
                    const action = unmatchedActions.get(item.rowIndex)
                    const isIgnored = ignoredRows.has(item.rowIndex)
                    return (
                      <TableRow key={item.rowIndex} className={isIgnored ? 'opacity-50' : ''}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="font-medium">
                              {item.extractedName || item.identifiers.name || 'Okänt namn'}
                            </div>
                            {item.identifiers.uuid && (
                              <div className="flex items-center gap-1 text-xs text-blue-600">
                                <Hash className="h-3 w-3" />
                                {item.identifiers.uuid}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {isIgnored ? (
                            <Badge variant="outline" className="text-gray-500 bg-gray-50">
                              Ignorerad
                            </Badge>
                          ) : action ? (
                            <Badge variant="outline" className="text-blue-700 bg-blue-50">
                              {action.action === 'create_new' ? 'Skapar ny förare' :
                               action.action === 'select_existing' ? 'Länkar till befintlig' : ''}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-yellow-700 bg-yellow-50">
                              Kräver åtgärd
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {isIgnored ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onUnignoreRow(item.rowIndex)}
                              className="text-xs px-2 py-1 h-auto text-blue-600 hover:text-blue-700"
                            >
                              Ångra ignorera
                            </Button>
                          ) : (
                            <div className="flex gap-2">
                              <Select
                                value={action?.action || ''}
                                onValueChange={(actionType) => {
                                  if (actionType === 'ignore') {
                                    onIgnoreRow(item.rowIndex)
                                  } else if (actionType === 'create_new') {
                                    const newDriverName = item.extractedName || item.identifiers.name || `Driver ${item.rowIndex + 1}`
                                    onUnmatchedActionChange(item.rowIndex, 'create_new', { newDriverName })
                                  } else if (actionType === 'link') {
                                    onUnmatchedActionChange(item.rowIndex, 'select_existing', { selectedDriverId: '' })
                                  }
                                }}
                              >
                                <SelectTrigger className="h-auto py-1 px-2 text-xs w-40">
                                  <SelectValue>
                                    {action?.action === 'create_new' ? 'Skapa ny förare' :
                                    action?.action === 'select_existing' ? 'Länka till förare' :
                                    'Välj åtgärd'}
                                  </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="create_new">Skapa ny förare</SelectItem>
                                  <SelectItem value="link">Länka till förare</SelectItem>
                                  <SelectItem value="ignore">Ignorera förare</SelectItem>
                                </SelectContent>
                              </Select>

                              {action?.action === 'select_existing' && (
                                <Select
                                  value={action?.selectedDriverId || ''}
                                  onValueChange={(driverId) => {
                                    onUnmatchedActionChange(item.rowIndex, 'select_existing', { selectedDriverId: driverId })
                                  }}
                                >
                                  <SelectTrigger className="h-auto py-1 px-2 text-xs w-40">
                                    <SelectValue placeholder="Välj förare" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {availableDrivers.map(driver => (
                                      <SelectItem key={driver.id} value={driver.id}>
                                        {driver.primaryName}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              )}
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
                </Table>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
